import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_JS_LOADER_OPTIONS } from "../../../config/googleMapsJsLoaderOptions";
import { FiSearch, FiChevronDown, FiList, FiMap, FiChevronRight } from "react-icons/fi";
import { categoryTabs } from "../data/mockData";
import MapListView from "./MapListView";
import axiosInstanceAuth from "../../../apiInstances/axiosInstanceAuth";

const NIGERIA_CENTER = { lat: 9.082, lng: 8.6753 };

const CITY_COORDS = {
  lagos: { lat: 6.5244, lng: 3.3792 },
  "victoria island": { lat: 6.4281, lng: 3.4219 },
  lekki: { lat: 6.4698, lng: 3.5852 },
  ikoyi: { lat: 6.455, lng: 3.434 },
  abuja: { lat: 9.0579, lng: 7.4951 },
  "port harcourt": { lat: 4.8156, lng: 7.0498 },
  kano: { lat: 12.0022, lng: 8.592 },
  ibadan: { lat: 7.3775, lng: 3.947 },
  "benin city": { lat: 6.335, lng: 5.6037 },
  owerri: { lat: 5.5804, lng: 7.9607 },
  ajah: { lat: 6.4584, lng: 3.6015 },
  ikorodu: { lat: 6.8923, lng: 3.7172 },
  surulere: { lat: 6.5059, lng: 3.3551 },
  "lagos island": { lat: 6.4541, lng: 3.3947 },
};

function resolveCoords(locationStr) {
  if (!locationStr) return null;
  const key = locationStr.toLowerCase().trim();
  if (CITY_COORDS[key]) return CITY_COORDS[key];
  for (const city of Object.keys(CITY_COORDS)) {
    if (key.includes(city) || city.includes(key)) return CITY_COORDS[city];
  }
  return null;
}

function groupByLocation(properties) {
  const map = {};
  for (const p of properties) {
    const loc = (p.suburb || p.location || p.city || p.street_address_name || "").trim();
    if (!loc) continue;
    if (!map[loc]) map[loc] = { label: loc, count: 0, coords: resolveCoords(loc) };
    map[loc].count += 1;
  }
  return Object.values(map).filter((g) => g.coords !== null);
}

const ExploreMapPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_JS_LOADER_OPTIONS);

  const category = searchParams.get("category") || "New Apartments";
  const locationParam = searchParams.get("location") || "";
  const userId = searchParams.get("userId") || localStorage.getItem("userId") || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [mapInstance, setMapInstance] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [activeView, setActiveView] = useState(searchParams.get("tab") === "list" ? "list" : "map");
  const [activeCategory, setActiveCategory] = useState(category);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const params = { category: activeCategory };
        if (locationParam) params.location = locationParam;
        if (userId) params.userId = userId;

        const res = await axiosInstanceAuth.get("Agency/properties", { params });
        if (res?.data?.status) {
          setProperties(res.data.data || []);
        } else {
          setFetchError(res?.data?.message || "Failed to load properties.");
          setProperties([]);
        }
      } catch (err) {
        setFetchError(err?.response?.data?.message || err.message || "Failed to load properties.");
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [activeCategory, locationParam, userId]);

  const mapClusters = groupByLocation(properties);

  useEffect(() => {
    const handleOutside = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const onMapLoad = useCallback(
    (map) => {
      setMapInstance(map);
      const targets = mapClusters.length > 0 ? mapClusters : [];
      if (targets.length === 0) {
        map.setCenter(NIGERIA_CENTER);
        map.setZoom(6);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      targets.forEach(({ coords }) => bounds.extend(coords));
      map.fitBounds(bounds, { top: 80, right: 80, bottom: 100, left: 80 });
    },
    [mapClusters]
  );

  useEffect(() => {
    if (mapInstance && mapClusters.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      mapClusters.forEach(({ coords }) => bounds.extend(coords));
      mapInstance.fitBounds(bounds, { top: 80, right: 80, bottom: 100, left: 80 });
    }
  }, [mapClusters, mapInstance]);

  const handleSearchThisArea = () => {
    if (!mapInstance) return;
    const center = mapInstance.getCenter();
    navigate(
      `/new-homes/results?search=${encodeURIComponent(`${center.lat().toFixed(4)},${center.lng().toFixed(4)}`)}`
    );
  };

  const getClusterIcon = (count) => ({
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: "#2563eb",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2.5,
    scale: count > 20 ? 22 : count > 10 ? 18 : 14,
    labelOrigin: new window.google.maps.Point(0, 0),
  });

  return (
    <div className={`flex flex-col ${activeView === "map" ? "h-[calc(100vh-64px)]" : "min-h-screen"}`}>
      {/* breadcrumb + top bar */}
      <div className="container">
        <nav className="flex items-center gap-4 text-md text-gray-500 pt-4">
          <button onClick={() => navigate("/")} className="hover:text-blue-600">
            Home
          </button>
          <span>/</span>
          <button onClick={() => navigate("/new-homes")} className="hover:text-blue-600">
            New Apartments
          </button>
          <span>/</span>
          <span className="flex items-center gap-1 text-gray-800 font-medium">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            {locationParam ? locationParam.charAt(0).toUpperCase() + locationParam.slice(1) : "Nigeria"}
          </span>
        </nav>

        <div className="flex items-center gap-3 pb-8 pt-5">
          {/* search */}
          <div className="flex items-center flex-1 max-w-5xl bg-white border-[1.5px] border-[#EAEAEA] rounded-xl px-4 py-3">
            <FiSearch size={16} className="text-[#097FE6] flex-shrink-0 mr-2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by region, suburb or postcode"
              className="flex-1 text-base text-gray-600 placeholder-gray-400 outline-none bg-transparent"
            />
          </div>

          {/* category dropdown */}
          <div className="relative" ref={categoryRef}>
            <button
              onClick={() => setCategoryOpen((o) => !o)}
              className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium hover:bg-gray-50 transition whitespace-nowrap"
              style={{ border: "1.5px solid #EAEAEA", color: "#000929" }}
            >
              {activeCategory}
              <FiChevronDown
                size={14}
                className={`transition-transform duration-200 ${categoryOpen ? "rotate-180" : ""}`}
                style={{ color: "rgba(0,9,41,0.5)" }}
              />
            </button>

            {categoryOpen && (
              <ul
                className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-xl z-50 overflow-hidden min-w-[180px]"
                style={{ border: "1.5px solid #EAEAEA" }}
              >
                {categoryTabs
                  .filter((t) => t.label !== "Explore")
                  .map((tab) => (
                    <li
                      key={tab.label}
                      onClick={() => {
                        setActiveCategory(tab.label);
                        setCategoryOpen(false);
                      }}
                      className="px-4 py-3 text-sm cursor-pointer transition-colors duration-100"
                      style={{
                        color: activeCategory === tab.label ? "#2563eb" : "#000929",
                        background: activeCategory === tab.label ? "#eff6ff" : "#fff",
                        fontWeight: activeCategory === tab.label ? 600 : 400,
                      }}
                      onMouseEnter={(e) => {
                        if (activeCategory !== tab.label) e.currentTarget.style.background = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        if (activeCategory !== tab.label) e.currentTarget.style.background = "#fff";
                      }}
                    >
                      {tab.label}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* list / map toggle */}
          <div className="relative flex items-center w-[112px] h-[50px] p-[4px] gap-[4px] bg-[#F3F6F9] rounded-[13px]">
            <div
              className={`absolute top-[4px] bottom-[4px] w-[calc(50%-6px)] bg-white rounded-[9px] shadow-sm transition-all duration-300 ${
                activeView === "list" ? "left-[4px]" : "left-[calc(50%+2px)]"
              }`}
            />
            <button
              onClick={() => setActiveView("list")}
              className={`relative z-10 flex-1 flex items-center justify-center rounded-[9px] transition ${
                activeView === "list" ? "text-black" : "text-gray-500"
              }`}
            >
              <FiList size={20} />
            </button>
            <button
              onClick={() => setActiveView("map")}
              className={`relative z-10 flex-1 flex items-center justify-center rounded-[9px] transition ${
                activeView === "map" ? "text-black" : "text-gray-500"
              }`}
            >
              <FiMap size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* list view */}
      {activeView === "list" && (
        <MapListView
          onBack={() => setActiveView("map")}
          activeCategory={activeCategory}
          properties={properties}
          loading={loading}
          error={fetchError}
        />
      )}

      {/* map */}
      <div className={`relative flex-1 ${activeView === "list" ? "hidden" : ""}`}>
        {(!isLoaded || loading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center text-red-500">
            Map unavailable.
          </div>
        )}
        {fetchError && !loading && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-red-50 text-red-600 text-sm px-4 py-2 rounded-xl shadow">
            {fetchError}
          </div>
        )}

        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={NIGERIA_CENTER}
            zoom={6}
            onLoad={onMapLoad}
            onClick={() => setSelectedMarker(null)}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            {mapClusters.map((cluster, i) => (
              <Marker
                key={`${cluster.label}-${i}`}
                position={cluster.coords}
                label={{
                  text: String(cluster.count),
                  color: "#ffffff",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
                icon={getClusterIcon(cluster.count)}
                onClick={() => setSelectedMarker(cluster)}
              />
            ))}
          </GoogleMap>
        )}

        {/* selected marker popup */}
        {selectedMarker && (
          <div className="absolute top-4 left-4 z-20 bg-white rounded-xl shadow-lg px-4 py-3 min-w-[160px]">
            <p className="font-semibold text-gray-900 text-sm">{selectedMarker.label}</p>
            <p className="text-blue-600 text-xs mt-1">{selectedMarker.count} properties</p>
            <button
              onClick={() =>
                navigate(`/new-homes/results?search=${encodeURIComponent(selectedMarker.label)}`)
              }
              className="mt-2 text-xs text-blue-600 hover:underline font-medium"
            >
              View listings →
            </button>
          </div>
        )}

        {/* search this area */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <button
            onClick={handleSearchThisArea}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-6 py-3 rounded-full shadow-xl transition-colors"
          >
            Search this area
            <FiChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExploreMapPage;
